import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase/client';

const schema = z
  .object({
    password: z.string().min(8, 'Mínimo 8 caracteres'),
    confirm: z.string().min(1, 'Confirma la contraseña'),
  })
  .refine((d) => d.password === d.confirm, {
    message: 'Las contraseñas no coinciden',
    path: ['confirm'],
  });

type Values = z.infer<typeof schema>;

export const Route = createFileRoute('/_auth/reset-password/confirm')({
  component: ResetPasswordConfirmPage,
});

function ResetPasswordConfirmPage() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);

  // Supabase inserta el token en el hash URL al redirigir desde el email.
  // La sesion se establece automaticamente en onAuthStateChange.
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setReady(true);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', confirm: '' },
  });

  const onSubmit = form.handleSubmit(async (v) => {
    const { error } = await supabase.auth.updateUser({ password: v.password });
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success('Contraseña actualizada correctamente');
    await navigate({ to: '/login' });
  });

  if (!ready) {
    return (
      <div className="text-center space-y-2">
        <p className="text-sm text-quiet">Verificando enlace de recuperación...</p>
        <p className="text-xs text-mute">
          Si este mensaje persiste, el link puede haber expirado. Solicita uno nuevo desde la página
          de{' '}
          <a href="/reset-password" className="text-accent hover:underline">
            restablecer contraseña
          </a>
          .
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
      <div>
        {/* i18n */}
        <h1 className="text-xl font-semibold text-ink">Nueva contraseña</h1>
        <p className="mt-1 text-sm text-quiet">Elige una contraseña segura.</p>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="new-password">Nueva contraseña</Label>
        <Input
          id="new-password"
          type="password"
          autoComplete="new-password"
          {...form.register('password')}
        />
        {form.formState.errors.password && (
          <span className="text-xs text-critical">{form.formState.errors.password.message}</span>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="confirm-password">Confirmar contraseña</Label>
        <Input
          id="confirm-password"
          type="password"
          autoComplete="new-password"
          {...form.register('confirm')}
        />
        {form.formState.errors.confirm && (
          <span className="text-xs text-critical">{form.formState.errors.confirm.message}</span>
        )}
      </div>

      <Button type="submit" disabled={form.formState.isSubmitting}>
        {form.formState.isSubmitting ? 'Guardando...' : 'Guardar contraseña'}
      </Button>
    </form>
  );
}
